import { DecisionAction, DecisionDetailItem, NextBestDecision } from "../../models/home";
import { FrontendLoggerService } from "../frontend-logger.service";
import { DecisionActionCatalogOwner, DecisionActionLike } from "./home-decision-action.types";

export class HomeDecisionActionCatalog {
  private readonly fallbackWarnings = new Set<string>();

  constructor(private readonly logger: FrontendLoggerService) {}

  getPrimaryAction(decision: DecisionActionCatalogOwner | null): DecisionActionLike | null {
    if (!decision) return null;
    if (decision.nextBestAction) return decision.nextBestAction;
    return this.hasOperationalCatalog(decision, "primary-action")
      ? this.pickAction(decision.availableActions ?? [])
      : null;
  }

  getPrimaryActionForNextDecision(nextDecision: NextBestDecision | null): DecisionActionLike | null {
    if (nextDecision?.nextAction) return nextDecision.nextAction;
    if (!nextDecision?.decision) return null;
    if (nextDecision.decision.nextBestAction) return nextDecision.decision.nextBestAction;
    return this.hasOperationalCatalog(nextDecision.decision, "hero", nextDecision.decision.decisionId)
      ? this.pickAction(nextDecision.decision.availableActions ?? [])
      : null;
  }

  getOperationalActions(decision: DecisionDetailItem | null): readonly DecisionAction[] {
    const primaryActionKey = decision?.nextBestAction?.actionKey ?? null;
    return this.getDetailActions(decision)
      .filter((action) => this.isActionRenderable(action) && action.actionKey !== primaryActionKey);
  }

  getSecondaryActions(decision: DecisionDetailItem | null): readonly DecisionAction[] {
    const primaryActionKey = this.getPrimaryAction(decision)?.actionKey;
    return this.getDetailActions(decision).filter((action) =>
      action.actionKey !== primaryActionKey && action.placement !== "danger" && action.kind !== "remediation" &&
      this.isActionRenderable(action) && action.enabled
    );
  }

  getRemediationActions(decision: DecisionDetailItem | null): readonly DecisionAction[] {
    return this.getDetailActions(decision).filter((action) => action.kind === "remediation" && this.isActionRenderable(action));
  }

  getDangerActions(decision: DecisionDetailItem | null): readonly DecisionAction[] {
    return this.getDetailActions(decision).filter((action) => action.placement === "danger" && this.isActionRenderable(action));
  }

  getDisabledActions(decision: DecisionDetailItem | null): readonly DecisionAction[] {
    return this.getDetailActions(decision).filter((action) => !action.enabled || this.requiresMissingSchemaFallback(action));
  }

  canExecuteHeroAction(action: DecisionActionLike | null): boolean {
    if (!action?.enabled || this.requiresForm(action) || action.confirmationRequired) return false;
    return action.riskLevel === "low" || action.executionMode === "direct";
  }

  requiresForm(action: DecisionActionLike | null): boolean {
    return !!action?.requiresInput;
  }

  getDetailActions(decision: DecisionDetailItem | null): readonly DecisionAction[] {
    if (!decision) return [];
    if (this.hasOperationalCatalog(decision, "detail", decision.decisionId)) return decision.availableActions ?? [];
    return decision.nextBestAction ? [decision.nextBestAction] : [];
  }

  pickAction(actions: readonly DecisionActionLike[], selectedActionKey?: string | null): DecisionActionLike | null {
    if (!actions.length) return null;
    const selected = selectedActionKey ? actions.find((action) => action.actionKey === selectedActionKey) : null;
    return selected ?? actions.find((action) => action.placement === "primary" && action.enabled) ??
      actions.find((action) => action.enabled && action.placement !== "danger") ??
      actions.find((action) => action.placement === "primary") ?? actions[0] ?? null;
  }

  hasOperationalCatalog(
    owner: DecisionActionCatalogOwner | null | undefined,
    surface: string,
    decisionId?: string | null
  ): owner is DecisionActionCatalogOwner & { availableActions: readonly DecisionActionLike[] } {
    if (!owner) return false;
    if (Array.isArray(owner.availableActions) && owner.availableActions.length > 0) return true;
    this.warnCatalogFallback(surface, decisionId, Array.isArray(owner.availableActions) ? "empty" : "missing");
    return false;
  }

  isActionRenderable(action: DecisionAction): boolean {
    return !!action.actionKey && !!action.label;
  }

  warnCatalogFallback(surface: string, decisionId: string | null | undefined, catalogState: "empty" | "missing"): void {
    const key = `${surface}:${decisionId ?? "unknown"}:${catalogState}`;
    if (this.fallbackWarnings.has(key)) return;
    this.fallbackWarnings.add(key);
    this.logger.warn(
      "[HomeDecisionActionUxService] Falling back to the legacy nextBestAction path because availableActions is missing or empty.",
      { decisionId: decisionId ?? undefined, surface, catalogState }
    );
  }

  requiresMissingSchemaFallback(action: DecisionActionLike): boolean {
    return this.requiresForm(action) && !this.hasInputSchema(action);
  }

  hasInputSchema(action: DecisionActionLike): action is DecisionAction {
    return "inputSchema" in action && !!action.inputSchema;
  }

}
